/**
 * Lead scoring, as TanStack Query hooks: the rules, the bands and decay, and
 * the preview over recent leads.
 *
 * Scores are internal. A buyer, supplier or pending login is refused by the
 * server everywhere here, so these hooks are only switched on for members who
 * see the whole organisation (`seesScores`).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from './client';
import type { components } from './schema';

type S = components['schemas'];
export type ScoringRule = S['ScoringRule'];
export type ScoreObject = S['ScoreObject'];
export type ScoringSettings = S['ScoringSettings'];
export type ScoreBand = S['ScoreBand'];
export type ScoreBandRef = S['ScoreBandRef'];
export type ScoreContribution = S['ScoreContribution'];
export type ScorePreviewResponse = S['ScorePreviewResponse'];
export type ScorePreviewBody = S['ScorePreviewBody'];
export type DraftScoringRule = S['DraftScoringRule'];
export type CreateScoringRuleBody = S['CreateScoringRuleBody'];
export type UpdateScoringRuleBody = S['UpdateScoringRuleBody'];
export type PersonScore = S['PersonScore'];
export type LeadScoreDetail = S['LeadScoreDetail'];

/** The organisation's rules, submission rules first, each in order. */
export function useScoringRules(enabled = true) {
  return useQuery({
    queryKey: ['scoring', 'rules'],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/scoring/rules', { params: { query: {} } }), 'ScoringRuleListResponse'),
    staleTime: 60 * 1000,
    enabled,
  });
}

/** Bands, decay and the person score's base. */
export function useScoringSettings(enabled = true) {
  return useQuery({
    queryKey: ['scoring', 'settings'],
    queryFn: async () => unwrap(await api.GET('/api/v1/settings/scoring', {}), 'ScoringSettings'),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

/** Every rule and settings write, each refreshing what shows scores. */
export function useScoringEdits() {
  const qc = useQueryClient();
  const done = () => {
    qc.invalidateQueries({ queryKey: ['scoring'] });
  };
  const create = useMutation({
    mutationFn: async (body: CreateScoringRuleBody) =>
      unwrap(await api.POST('/api/v1/scoring/rules', { body }), 'ScoringRule'),
    onSuccess: done,
  });
  const update = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: UpdateScoringRuleBody }) =>
      unwrap(await api.PATCH('/api/v1/scoring/rules/{id}', { params: { path: { id } }, body }), 'ScoringRule'),
    onSuccess: done,
  });
  const activate = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) =>
      unwrap(
        await api.POST('/api/v1/scoring/rules/{id}/activate', { params: { path: { id } }, body: { active } }),
        'ScoringRule',
      ),
    onSuccess: done,
  });
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.DELETE('/api/v1/scoring/rules/{id}', { params: { path: { id } } });
      if (res.error !== undefined) {
        const e = res.error as { detail?: string };
        throw new Error(e.detail ?? 'Delete failed');
      }
      return id;
    },
    onSuccess: done,
  });
  const reorder = useMutation({
    mutationFn: async ({ object, ids }: { object: ScoreObject; ids: string[] }) =>
      unwrap(await api.POST('/api/v1/scoring/rules/reorder', { body: { object, ids } }), 'ScoringRuleListResponse'),
    onSuccess: done,
  });
  const saveSettings = useMutation({
    mutationFn: async (body: ScoringSettings) =>
      unwrap(await api.PUT('/api/v1/settings/scoring', { body }), 'ScoringSettings'),
    onSuccess: (s) => {
      qc.setQueryData(['scoring', 'settings'], s);
      // Badges are banded on the server.
      qc.invalidateQueries({ queryKey: ['leads'] });
      qc.invalidateQueries({ queryKey: ['people'] });
    },
  });
  return { create, update, activate, remove, reorder, saveSettings };
}

/**
 * How recent leads would score with the saved rules, or with a draft in
 * their place. Read-only on the server. Keyed by the body, so an edit that
 * changes nothing does not ask again; the caller debounces typing.
 */
export function useScorePreview(body: ScorePreviewBody | null, enabled = true) {
  return useQuery({
    queryKey: ['scoring', 'preview', body],
    queryFn: async () =>
      unwrap(await api.POST('/api/v1/scoring/preview', { body: body ?? {} }), 'ScorePreviewResponse'),
    enabled: enabled && body !== null,
    staleTime: 30 * 1000,
    placeholderData: (prev) => prev,
  });
}
